import { splitStringByChars } from '@home/utils/utils';
import { getCountryFlag } from '@home/_config/countries';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getNullableMoment } from '@intelligence/v3/utils/date';
import { getUSOnlyRegionFilterValue } from '@intelligence/v3/utils/region';
import { GENRE_SOURCE_REVERSE_MAP, normalizeTableGenreSource } from '@intelligence/v3/utils/genre';
import { DATASOURCE_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics';
import CommonColumns from '@intelligence/pages/TopCharts/_config/TableCloums/CommonCloums';
import { AppMagicGenreTagType } from '@intelligence/components/TableComponents/components/AppMagicGenreTagList';

import type { IGenreRes } from '@intelligence/types/common';
import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';
import type { IRevenueGrowthValue } from '@intelligence/components/RevenueGrowth';
import type { MainGenre, SubGenre } from '@intelligence/components/GenreSelectMiddleware/types';
import type { RecordItemType } from '@intelligence/components/SearchRecent/type';
import type {
  IMetricExplanationTipsClickKey,
  IMetricExplanationTipsClickPayload,
} from '@intelligence/v3/components/MetricExplanationTips';

/**
 * 默认勾选国家配置
 */
const DEFAULT_TOP_CONFIG: {
  /**
   * 开始索引
   */
  start: number;
  /**
   * 数量
   */
  count: number;
} = {
  /**
   * 默认从第n个开始勾选
   */
  start: 0,
  /**
   * 默认勾选n个
   */
  count: 6,
};

/**
 * 获取前n个列表数据（通常是作为宽表的默认勾选数据）
 * @param list 列表数据
 * @param topConfig 前n个的配置
 * @return 前n个列表数据
 */
export const getTopList = <T = any>(list?: T[] | null, topConfig = DEFAULT_TOP_CONFIG): T[] => {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.slice(topConfig.start, topConfig.start + topConfig.count);
};

/**
 * @description 搜索返回值的formater方法
 * @param data 搜索返回数据
 * @param listKey 列表数据的取值字段
 * @return 搜索浮窗列表渲染的列表数据
 */
export const transTableDataForSearch = (data: any, listKey = 'list'): RecordItemType[] => {
  const list = data?.[listKey];
  if (!Array.isArray(list) || list.length === 0) return [];

  return list.map((item) => {
    const id = item?.id || '';
    const name = item?.name || '';
    const cover = item?.cover || '';
    const publisherAddr = item?.publisher_addr || '';
    const publisher = item?.publisher || '';

    return {
      id,
      imgSrc: cover,
      lineOneStr: name,
      lineTwoImg: getCountryFlag(publisherAddr),
      lineTwoImgName: publisherAddr,
      lineTwoStr: publisher,
    };
  });
};

/**
 * 给数据列表的每一项增加rank序号
 * @param list 数据列表
 * @return list 数据列表（带rank序号）
 */
export const addRank = <T extends { rank: number } = any>(list: T[] | null): T[] => {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((item, index) => {
    return { ...item, rank: index + 1 };
  });
};

/**
 * 给数据列表的每一项增加rank序号（适用接口分页）
 * @param list 数据列表
 * @param paginationFetchParams 分页参数
 * @return list 数据列表（带rank序号）
 */
export const addRankPageable = <T extends { rank: number } = any>(
  list: T[] | null,
  paginationFetchParams: IPaginationFetchParams,
): T[] => {
  if (!Array.isArray(list) || list.length === 0) return [];
  const page = paginationFetchParams?.page ?? Number.NaN;
  const pageSize = paginationFetchParams?.page_size ?? Number.NaN;
  if (Number.isNaN(page) || Number.isNaN(pageSize)) return list;
  return list.map((item, index) => {
    return { ...item, rank: (page - 1) * pageSize + (index + 1) };
  });
};

/**
 * 把GSD Publisher Shared筛选器的value转换为接口需要的格式
 * GSD Publisher Shared筛选器value为string类型，接口要求入参为string[]类型
 * @param value GSD Publisher Shared筛选器的value
 * @return 接口需要的入参格式
 */
export const formatGSDTypeFilterValue = (value?: string): string[] => {
  if (!value) return [];
  if (value?.toLowerCase() === 'all') return [];
  return [value];
};

export const calcMainGenres = (
  source: string,
  mainGenres: MainGenre[],
  subGenres: SubGenre[],
  genresOptionsData?: IGenreRes | null,
) => {
  if (!Array.isArray(mainGenres) || mainGenres.length === 0) return [];
  const genresOptions = genresOptionsData?.[GENRE_SOURCE_REVERSE_MAP?.[source]] || [];
  const subGenresValues = subGenres?.filter((item) => !!item?.checked)?.map((item) => item?.value) || [];

  return mainGenres.map((mainGenre) => {
    const genresOption = genresOptions?.find((item) => item?.main_genre?.genre === mainGenre?.value);
    const genresSubOptions = genresOption?.sub_genre || [];
    const genresSubOptionsChecked = genresSubOptions?.filter((item) => {
      return subGenresValues.includes(item?.genre);
    });
    const genresSubOptionsCheckedLength = genresSubOptionsChecked?.length ?? 0;
    const checked = genresSubOptionsCheckedLength > 0 && genresSubOptionsCheckedLength === genresSubOptions?.length;
    return { ...mainGenre, checked, indeterminate: !checked && genresSubOptionsCheckedLength > 0 };
  });
};

/**
 * 处理指标解释气泡内容的内部click事件，统一封装点击字段、点击事件名的判断分支逻辑
 * @param key 点击的字段
 * @param payload 点击的载荷，包含点击的事件名
 * @param config 其他配置，多用与调用者传入各种事件的处理
 */
export const handleMetricExplanationTipsClick = (
  key: IMetricExplanationTipsClickKey,
  payload?: IMetricExplanationTipsClickPayload,
  config?: {
    filterValue?: any;
    onClickSetRegionUSOnly?: (params: any) => void;
    onClickViewCalibrationData?: (metric: string) => void;
    gameId?: string;
  },
) => {
  console.log('handleMetricExplanationTipsClick called', { key, payload, config: !!config });

  const metricExplanationClickEventName = payload?.key;

  if (key === 'dataSource') {
    if (metricExplanationClickEventName === 'setRegionUSOnly') {
      console.log('handleMetricExplanationTipsClick: Processing setRegionUSOnly');
      const filterValue = config?.filterValue;
      const onClickSetRegionUSOnly = config?.onClickSetRegionUSOnly;
      const filterValueTmp = getUSOnlyRegionFilterValue(filterValue);
      onClickSetRegionUSOnly?.(filterValueTmp);
    }
  }

  if (key === 'definition') {
    if (metricExplanationClickEventName === 'viewCalibrationData') {
      console.log('handleMetricExplanationTipsClick: Processing viewCalibrationData', {
        metric: payload?.metric,
        hasCallback: !!config?.onClickViewCalibrationData,
      });
      const onClickViewCalibrationData = config?.onClickViewCalibrationData;
      const metric = payload?.metric || '';
      onClickViewCalibrationData?.(metric);
    }
  }
};

export const splitReportTagStr = (tags?: string[]): string[] => {
  if (isEmpty(tags)) return [];

  return tags!.flatMap((tag) => splitStringByChars(tag));
};

/**
 * 获取前一周期的指标值的键（环比计算时涉及到的前一周期）
 * @param valueKey - 当前指标值的键
 * @return 前一周期的指标值的键
 */
export const getComparePrevFieldKey = (valueKey?: string): string => {
  if (!valueKey) {
    return '';
  }

  if (valueKey === 'value') {
    return 'prev_value';
  }

  return `${valueKey}_prev`;
};

export interface IMetricValueRangeFilterMetadata {
  metric: string;
  metricValue: IRevenueGrowthValue;
}

const RATIO_METRIC_KEYS = [DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_APP_USAGE];

export const buildMetricFiltersFetchParams = (
  list?: IMetricValueRangeFilterMetadata[] | null,
): IMetricValueRangeFilter[] => {
  if (!Array.isArray(list) || !list?.length) {
    return [];
  }

  return list
    .filter((item) => {
      const { metric, metricValue } = item || {};
      const { opt, value } = metricValue || {};
      const numValue = Number.parseFloat(value as string);
      return !!metric && !!opt && !!value && !Number.isNaN(numValue);
    })
    .map((item) => {
      const { metric, metricValue } = item || {};
      const { opt, value } = metricValue || {};
      return {
        metric,
        opt,
        value: Number.parseFloat(value as string),
        ratio_type: RATIO_METRIC_KEYS.includes(metric) ? 1 : 0,
      };
    });
};

export const buildSensorTowerTagsParams = (tags?: any[] | null): ITagValue[] => {
  if (isEmpty(tags)) return [];

  return tags!.map((tag) => {
    const name = tag?.tag_name || '';
    return { name, children: buildSensorTowerTagsParams(tag?.Tags) };
  });
};

export const transMetricDataGamesForSearch = (
  res: IUnwrapPromise<IBaseResponse<IPaginationData<IMetricDataGame>>>,
): RecordItemType[] => {
  const games = res?.data?.list;

  if (!Array.isArray(games) || games.length === 0) {
    return [];
  }

  return games.map((game) => {
    const publisherCountryCode = game?.publisher_country_abbre || '';
    return {
      id: game?.game_id || '',
      imgSrc: game?.cover || '',
      lineOneStr: game?.game_name || '',
      lineTwoImg: getCountryFlag(publisherCountryCode),
      lineTwoImgName: publisherCountryCode,
      lineTwoStr: game?.publisher || '',
    };
  });
};

export const sliceTableCellGames = <T = any>(list?: T[] | null, num = 4): T[] => {
  if (isEmpty(list)) {
    return [];
  }

  return list!.slice(0, num);
};

/**
 * 获取指定日期粒度下，多个指标的有数据的日期范围（用于计算某个指标页面在指定日期粒度下有数据的日期范围）
 * @param granularity - 日期粒度
 * @param metrics - 多个指标
 * @param metricsDateRangeMap - 指标有数据的日期范围映射
 * @return 指定日期粒度下，多个指标的有数据的日期范围
 */
export const getDateRangeOfMetricsPage = (
  granularity: IGranularity,
  metrics: string[],
  metricsDateRangeMap: IRecord<IMetricDateRange>,
): { minDate: string; maxDate: string } => {
  if (isEmpty(metrics)) {
    return { minDate: '', maxDate: '' };
  }

  if (isEmpty(metricsDateRangeMap)) {
    return { minDate: '', maxDate: '' };
  }

  let minDate = '';
  let maxDate = '';

  metrics.forEach((metric) => {
    const key = `${granularity}__${metric}`;
    const dateRange = metricsDateRangeMap[key] || {};
    const metricMinDate = dateRange?.min_date || '';
    const metricMaxDate = dateRange?.max_date || '';

    if (!minDate || metricMinDate < minDate) {
      minDate = metricMinDate;
    }

    if (!maxDate || metricMaxDate > maxDate) {
      maxDate = metricMaxDate;
    }
  });

  return { minDate, maxDate };
};

/**
 * 获取有效的日期范围
 * @param dateRangeFetchParams - 日期范围参数
 * @param config - 日期范围校验调整配置
 * @param config.minDate - 最小日期
 * @param config.maxDate - 最大日期
 */
export const getValidDateRangeOfMetricsPage = (
  dateRangeFetchParams?: IDateRangeFetchParams,
  config?: { minDate: string; maxDate: string },
): IDateRangeFetchParams => {
  if (isEmpty(dateRangeFetchParams)) {
    return { granularity: '', start_date: '', end_date: '' };
  }

  let startDate = dateRangeFetchParams?.start_date || '';
  let endDate = dateRangeFetchParams?.end_date || '';
  const minDate = config?.minDate || '';
  const maxDate = config?.maxDate || '';

  if (!!startDate) {
    if (startDate < minDate) {
      startDate = minDate;
    }

    if (startDate > maxDate) {
      startDate = maxDate;
    }
  }

  if (!!endDate) {
    if (endDate < minDate) {
      endDate = minDate;
    }

    if (endDate > maxDate) {
      endDate = maxDate;
    }
  }

  return {
    ...dateRangeFetchParams,
    granularity: dateRangeFetchParams?.granularity || '',
    start_date: startDate,
    end_date: endDate,
  };
};

/**
 * 根据 genreSource 获取品类列配置
 * @param params.isGameDetailsMode - 是否为游戏详情数据独立加载模式
 * @param params.gameDetailsLoading - 游戏详情是否加载中
 * @param params.gameDetailsMapping - 游戏详情数据映射
 * @param params.genreSource - 品类数据源
 * @param params.formatMessage - 国际化方法
 * @param params.onAppMagicTagClick - App Magic 品类标签点击回调
 * @returns 品类列配置数组
 */
export const getGenreColumns = (params: {
  isGameDetailsMode?: boolean;
  gameDetailsLoading?: boolean;
  gameDetailsMapping?: IRecord<IRecord>;
  genreSource: IGenreSource;
  genreOptionsMap?: IRecord<IOptionWithChildren[]>;
  locale?: string;
  formatMessage: Function;
  onAppMagicTagClick?: (
    type: AppMagicGenreTagType,
    superTag: string,
    majorTag?: string,
    midTag?: string,
    subTag?: string,
  ) => void;
}) => {
  const {
    isGameDetailsMode = true,
    gameDetailsLoading = true,
    gameDetailsMapping,
    genreSource,
    genreOptionsMap,
    locale,
    formatMessage,
    onAppMagicTagClick,
  } = params || {};
  const normalizedGenreSource = normalizeTableGenreSource(genreSource);
  const commonParams = {
    isGameDetailsMode,
    gameDetailsLoading,
    gameDetailsMapping,
    genreOptionsMap,
    locale,
    formatMessage,
  };

  if (normalizedGenreSource === 'appmagic') {
    return [
      CommonColumns.AppMagicSuperGenreColumn({ ...commonParams, onTagClick: onAppMagicTagClick }),
      CommonColumns.AppMagicMajorGenreColumn({ ...commonParams, onTagClick: onAppMagicTagClick }),
    ];
  }

  return [
    CommonColumns.GenreColumn({
      ...commonParams,
      genreSource: normalizedGenreSource,
    }),
  ];
};

/**
 * 校验是否为有效的游戏id（unified_id、edition_id）
 * @param gameId - 游戏id
 * @return 是否为有效的游戏id
 */
export const validateGameId = (gameId?: string): boolean => {
  if (!gameId?.trim()) {
    return false;
  }

  return /^([e|u])(\w+)$/g.test(gameId);
};

/**
 * 校验排序指标是否在 source_metrics 中，如果不在则取第一个指标
 * @param sourceMetrics 过滤后的 source_metrics
 * @param currentOrderSource 当前的排序数据源
 * @param currentOrderMetric 当前的排序指标
 * @returns 校验后的排序数据源和指标
 */
export const validateOrderFields = (
  sourceMetrics?: IRecord<string[]>,
  currentOrderSource?: string,
  currentOrderMetric?: string,
): { orderSource: string; orderMetric: string } => {
  if (isEmpty(sourceMetrics)) {
    return { orderSource: '', orderMetric: '' };
  }

  const sourceKeys = Object.keys(sourceMetrics || {});

  if (!sourceKeys.length) {
    return { orderSource: '', orderMetric: '' };
  }

  // 检查当前的排序指标是否在 source_metrics 中
  const isOrderValid =
    currentOrderSource && currentOrderMetric && sourceMetrics?.[currentOrderSource]?.includes(currentOrderMetric);

  if (isOrderValid) {
    return { orderSource: currentOrderSource, orderMetric: currentOrderMetric };
  }

  // 如果不在，取第一个数据源的第一个指标
  const firstSource = sourceKeys[0] || '';
  const firstMetric = sourceMetrics?.[firstSource]?.[0] || '';
  return { orderSource: firstSource, orderMetric: firstMetric };
};

/**
 * 获取环比的开始时间和结束时间
 * @param granularity 时间维度: daily/weekly/monthly
 * @param startDate 当前周期开始时间 (格式: YYYY-MM-DD)
 * @param endDate 当前周期结束时间 (格式: YYYY-MM-DD)
 * @returns [环比开始时间, 环比结束时间] 或 null
 */
export const calcRatioDateRange = (
  granularity?: string,
  startDate?: string,
  endDate?: string,
): [string, string] | null => {
  if (!granularity) {
    return null;
  }

  // 小时粒度需要保留时间部分，其他粒度使用日期格式
  const isHourlyGranularity = ['hourly', 'hour'].includes(granularity);
  const format = isHourlyGranularity ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD';
  const startDateM = getNullableMoment(startDate, format);
  const endDateM = getNullableMoment(endDate, format);

  if (!startDateM || !endDateM) {
    return null;
  }

  // 小时粒度：以上一个小时作为环比周期
  // hourly 数据范围为每天 01:00 ~ 23:59（无 00:00），
  // 当当前行为 01:00 时，上一时段应跨天到前一天的 23:59
  if (isHourlyGranularity) {
    const startHour = startDateM.hour();
    const startMinute = startDateM.minute();

    // 当前是 01:00 整点 → 上一时段为前一天 23:59
    if (startHour === 1 && startMinute === 0) {
      const ratioEndOfDay = startDateM.clone().subtract(1, 'days').endOf('day').format('YYYY-MM-DD HH:mm'); // 23:59
      return [ratioEndOfDay, ratioEndOfDay];
    }

    // 其他情况：正常减一小时
    const diffHours = endDateM.diff(startDateM, 'hours');
    const ratioEndDate = startDateM.clone().subtract(1, 'hours').format(format);
    const ratioStartDate = startDateM
      .clone()
      .subtract(Math.floor(diffHours) + 1, 'hours')
      .format(format);
    return [ratioStartDate, ratioEndDate];
  }

  // 日/周维度
  if (['daily', 'weekly', 'day', 'week'].includes(granularity)) {
    const diffHours = endDateM.diff(startDateM, 'hours');
    const ratioEndDate = startDateM.clone().subtract(1, 'days').format(format);
    const ratioStartDate = startDateM
      .clone()
      .subtract(Math.floor(diffHours / 24) + 1, 'days')
      .format(format);
    return [ratioStartDate, ratioEndDate];
  }

  // 月维度
  if (['monthly', 'monthly'].includes(granularity)) {
    const ratioEndDate = startDateM.clone().subtract(1, 'days').format(format);
    const diffMonths = endDateM.diff(startDateM, 'months');
    const ratioStartDate = startDateM
      .clone()
      .subtract(diffMonths + 1, 'months')
      .format(format);
    return [ratioStartDate, ratioEndDate];
  }

  return null;
};

/**
 * 判定 gameId 是否为 mobile 游戏
 * mobile 游戏 id 约定以字母 u 开头，忽略大小写
 * @param gameId - 游戏id
 * @return 是否为 mobile 游戏
 */
export const isMobileGameId = (gameId?: string): boolean => {
  if (!gameId?.trim()) {
    return false;
  }

  return /^u/i.test(gameId);
};

/**
 * 根据 entity type 过滤游戏 id
 * mobile 仅保留以字母 u 开头的游戏 id，忽略大小写
 * @param entityType - 游戏实体类型
 * @param gameIds - 游戏id 列表
 * @return 过滤后的游戏 id 列表
 */
export const filterGameIdsByEntityType = (entityType?: string, gameIds?: string[] | null): string[] => {
  if (!Array.isArray(gameIds) || gameIds.length === 0) {
    return [];
  }

  if (entityType === 'mobile') {
    return gameIds.filter(isMobileGameId);
  }

  return gameIds;
};

/**
 * 校验游戏详情页 URL 中的游戏 id 是否合法
 * 合法标准：非空且仅包含英文字母或数字
 * 当前 mobile / pc / console / combine 共用同一规则，后续可按 entityType 扩展不同的校验处理
 * @param entityType - 游戏实体类型（mobile / pc / console / combine）
 * @param gameId - 游戏id
 * @return 是否为合法的游戏 id
 */
export const validateGameId = (entityType?: string, gameId?: string): boolean => {
// 游戏详情页合法游戏 id 格式：仅包含英文字母或数字
  return /^[A-Za-z0-9]+$/ig.test(gameId ?? '');
};
